/**
 * Système offline-first :
 * - Détecte l'état de connexion (navigator.onLine + ping périodique)
 * - File d'attente des requêtes POST/PUT/DELETE échouées (réseau coupé)
 * - Persistance dans localStorage : survit à un refresh / fermeture d'onglet
 * - Replay automatique au retour de la connexion
 * - Notifications toast à l'utilisateur
 */

import axios from 'axios';
import { toast } from 'sonner';

const STORAGE_KEY = 'bigdem_offline_queue_v1';
const API = process.env.REACT_APP_BACKEND_URL;

// ─────────────────────────── État global ───────────────────────────
let _isOnline = typeof navigator !== 'undefined' ? navigator.onLine : true;
const _statusListeners = new Set();

export function getOnlineStatus() {
    return _isOnline;
}

export function subscribeOnlineStatus(cb) {
    _statusListeners.add(cb);
    cb(_isOnline);
    return () => _statusListeners.delete(cb);
}

function _setOnline(value) {
    if (_isOnline === value) return;
    _isOnline = value;
    _statusListeners.forEach(cb => { try { cb(value); } catch { /* ignore */ } });
    if (value) {
        flushQueue();
    }
}

// ─────────────────────────── File d'attente ───────────────────────────
function _loadQueue() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        return raw ? JSON.parse(raw) : [];
    } catch { return []; }
}

function _saveQueue(items) {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch { /* quota plein, ignore */ }
}

export function queueLength() {
    return _loadQueue().length;
}

export function enqueueRequest(req) {
    const items = _loadQueue();
    items.push({ ...req, id: Date.now() + Math.random().toString(36).slice(2) });
    _saveQueue(items);
    _statusListeners.forEach(cb => { try { cb(_isOnline); } catch { /* ignore */ } });
}

let _flushing = false;
export async function flushQueue() {
    if (_flushing) return;
    _flushing = true;
    try {
        let items = _loadQueue();
        let success = 0;
        while (items.length > 0) {
            const item = items[0];
            try {
                await axios({
                    method: item.method,
                    url: item.url,
                    data: item.data,
                    headers: item.headers || {},
                    withCredentials: true,
                });
                success += 1;
                items.shift();
                _saveQueue(items);
            } catch (e) {
                // Si erreur réseau → on laisse en queue, on attendra le prochain online
                if (!e.response) {
                    _setOnline(false);
                    break;
                }
                // Sinon erreur serveur (4xx/5xx) → on retire et on prévient
                console.error('Replay request failed (server error):', item.method, item.url, e.response.status);
                items.shift();
                _saveQueue(items);
            }
        }
        if (success > 0) {
            _statusListeners.forEach(cb => { try { cb(_isOnline); } catch { /* ignore */ } });
        }
    } finally { _flushing = false; }
}

// ─────────────────────────── Axios interceptor ───────────────────────────
function _isWriteRequest(config) {
    const m = (config.method || 'get').toLowerCase();
    return m === 'post' || m === 'put' || m === 'patch' || m === 'delete';
}

function _normalizeUrl(url) {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    return `${API}${url.startsWith('/') ? '' : '/'}${url}`;
}

axios.interceptors.response.use(
    (response) => {
        // Marche : on est forcément en ligne
        if (!_isOnline) _setOnline(true);
        return response;
    },
    (error) => {
        const config = error.config || {};
        const isNetworkError = !error.response;
        if (isNetworkError && _isWriteRequest(config)) {
            // Réseau coupé → enqueue
            const url = _normalizeUrl(config.url);
            // Évite de mettre en queue les requêtes d'auth (login/refresh)
            if (url.includes('/api/auth/login') || url.includes('/api/auth/refresh')) {
                return Promise.reject(error);
            }
            enqueueRequest({
                method: (config.method || 'post').toLowerCase(),
                url,
                data: config.data ? (typeof config.data === 'string' ? JSON.parse(config.data) : config.data) : undefined,
                headers: { 'Content-Type': 'application/json' },
            });
            _setOnline(false);
            // Renvoie une réponse "fictive" pour ne pas bloquer le formulaire
            return Promise.resolve({
                data: { _offline_queued: true },
                status: 202,
                statusText: 'Queued',
                headers: {},
                config,
            });
        }
        return Promise.reject(error);
    }
);

// ─────────────────────────── Détection online/offline ───────────────────────────
if (typeof window !== 'undefined') {
    window.addEventListener('online', () => _setOnline(true));
    window.addEventListener('offline', () => _setOnline(false));

    // Ping silencieux toutes les 30s pour vérifier la connexion réelle
    // (navigator.onLine est parfois optimiste)
    setInterval(async () => {
        try {
            await fetch(`${API}/api/health`, { method: 'GET', cache: 'no-store', credentials: 'omit' });
            if (!_isOnline) _setOnline(true);
            else if (queueLength() > 0) flushQueue();
        } catch {
            if (_isOnline) _setOnline(false);
        }
    }, 30000);

    // Au démarrage, si on a des requêtes en attente → tenter de les envoyer
    if (_isOnline && queueLength() > 0) {
        setTimeout(() => flushQueue(), 1500);
    }
}
